import { useState, useCallback, useEffect } from 'react';
import { api } from '@services';
import { AIAnalysisResult, AnalysisRequest } from '@features/analysis/types';

interface AnalysisExecutionState {
  isAnalyzing: boolean;
  analysisProgress: number;
  analysisStep: string;
  executionError: string | null;
  lastExecutionTime: Date | null;
}

interface AnalysisExecutionActions {
  runAnalysis: (request: AnalysisRequest) => Promise<AIAnalysisResult>;
  cancelAnalysis: () => void;
  clearExecutionError: () => void;
  resetExecutionState: () => void;
}

type UseAnalysisExecutionReturn = AnalysisExecutionState & AnalysisExecutionActions;

interface UseAnalysisExecutionOptions {
  maxRetries?: number;
  retryDelay?: number;
  onAnalysisStart?: () => void;
  onAnalysisComplete?: (result: AIAnalysisResult) => void;
  onAnalysisError?: (error: string) => void;
  onProgressUpdate?: (progress: number, step: string) => void;
}

/**
 * Consolidated hook for managing analysis execution
 * Handles running analysis, progress tracking, retries, and error handling
 */
export const useAnalysisExecution = (
  options: UseAnalysisExecutionOptions = {}
): UseAnalysisExecutionReturn => {
  const {
    maxRetries = 3,
    retryDelay = 2000,
    onAnalysisStart,
    onAnalysisComplete,
    onAnalysisError,
    onProgressUpdate
  } = options;

  // Execution State
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStep, setAnalysisStep] = useState('');
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [lastExecutionTime, setLastExecutionTime] = useState<Date | null>(null);

  // Cancellation flag
  const [isCancelled, setIsCancelled] = useState(false);

  // Progress update helper
  const updateProgress = useCallback((progress: number, step: string) => {
    setAnalysisProgress(progress);
    setAnalysisStep(step);
    onProgressUpdate?.(progress, step);
  }, [onProgressUpdate]);

  // Analysis execution with progress tracking
  const runAnalysis = useCallback(async (request: AnalysisRequest): Promise<AIAnalysisResult> => {
    if (!request.hotelId) {
      throw new Error('Cannot run analysis without a Hotel ID.');
    }

    // Reset state
    setIsAnalyzing(true);
    setIsCancelled(false);
    setExecutionError(null);
    setAnalysisProgress(0);
    setAnalysisStep('');

    // Notify start
    onAnalysisStart?.();
    updateProgress(5, 'Initializing analysis...');

    let attempts = 0;

    try {
      while (attempts < maxRetries && !isCancelled) {
        try {
          attempts++;

          updateProgress(10, `Starting analysis (attempt ${attempts}/${maxRetries})...`);

          // Simulate analysis steps for better UX
          const analysisSteps = [
            { progress: 20, step: 'Processing hotel data...' },
            { progress: 35, step: 'Analyzing financial documents...' },
            { progress: 50, step: 'Evaluating performance metrics...' },
            { progress: 65, step: 'Generating market insights...' },
            { progress: 80, step: 'Calculating valuations...' },
            { progress: 90, step: 'Finalizing analysis...' }
          ];

          // Update progress during API call
          const progressInterval = setInterval(() => {
            if (!isCancelled) {
              const currentStep = analysisSteps.find(step => step.progress > analysisProgress);
              if (currentStep) {
                updateProgress(currentStep.progress, currentStep.step);
              }
            }
          }, 1500);

          try {
            // Make the actual API call
            const response = await api.post(`/hotels/${request.hotelId}/ai-analysis`, request);

            // Clear progress interval
            clearInterval(progressInterval);

            if (isCancelled) {
              throw new Error('Analysis was cancelled');
            }

            updateProgress(95, 'Processing results...');

            // Validate response
            console.log('🔍 Validating analysis response:', {
              hasData: !!response.data,
              dataType: typeof response.data,
              dataKeys: response.data ? Object.keys(response.data) : [],
              hasMarketAnalysis: !!(response.data?.marketAnalysis),
              hasInvestmentScore: response.data?.investmentScore !== undefined,
              hasPropertyAssessment: !!(response.data?.propertyAssessment),
              hasValuationInsights: !!(response.data?.valuationInsights),
              rawData: response.data
            });

            if (typeof response.data === 'object' &&
                response.data !== null &&
                Object.keys(response.data).length > 0) {

              // More flexible validation - check for at least some analysis content
              const hasAnalysisContent = response.data.marketAnalysis ||
                                        response.data.propertyAssessment ||
                                        response.data.valuationInsights ||
                                        response.data.investmentScore !== undefined;

              if (hasAnalysisContent) {
                console.log('✅ Response validation passed');
                updateProgress(100, 'Analysis complete!');
                setLastExecutionTime(new Date());

                // Notify completion
                onAnalysisComplete?.(response.data);

                return response.data as AIAnalysisResult;
              } else {
                console.error('❌ Response validation failed - no analysis content found:', response.data);
                throw new Error('Analysis completed but response contains no recognizable analysis data.');
              }
            } else {
              console.error('❌ Response validation failed - invalid response structure:', response.data);
              throw new Error('Received invalid analysis data format from server.');
            }
          } catch (apiError) {
            clearInterval(progressInterval);
            throw apiError;
          }

        } catch (runError: any) {
          console.error(`Analysis attempt ${attempts}/${maxRetries} failed:`, runError);

          if (isCancelled) {
            throw new Error('Analysis was cancelled by user');
          }

          // If we've exhausted retries, throw the error
          if (attempts >= maxRetries) {
            let errorMessage = 'Failed to analyze hotel property.';

            if (runError.message === 'Network Error') {
              errorMessage = 'Network error: Cannot connect to the analysis server. Please check your internet connection and try again.';
            } else if (runError.response?.data?.error) {
              errorMessage = runError.response.data.error;
            } else if (typeof runError.message === 'string') {
              errorMessage = runError.message;
            }

            setExecutionError(errorMessage);
            onAnalysisError?.(errorMessage);

            throw new Error(errorMessage);
          }

          // Wait before retrying
          updateProgress(5, `Retrying in ${retryDelay / 1000} seconds...`);
          await new Promise((resolve) => setTimeout(resolve, retryDelay * attempts));
        }
      }

      // This should not be reached, but just in case
      const finalError = 'Analysis failed after all retry attempts';
      setExecutionError(finalError);
      throw new Error(finalError);

    } finally {
      // Always reset the analyzing state
      setIsAnalyzing(false);
    }

  }, [maxRetries, retryDelay, isCancelled, analysisProgress, onAnalysisStart, onAnalysisComplete, onAnalysisError, updateProgress]);

  // Cancel analysis
  const cancelAnalysis = useCallback(() => {
    setIsCancelled(true);
    setIsAnalyzing(false);
    setAnalysisProgress(0);
    setAnalysisStep('Analysis cancelled');
    setExecutionError('Analysis was cancelled by user');
  }, []);

  // Clear execution error
  const clearExecutionError = useCallback(() => {
    setExecutionError(null);
  }, []);

  // Reset execution state
  const resetExecutionState = useCallback(() => {
    setIsAnalyzing(false);
    setIsCancelled(false);
    setAnalysisProgress(0);
    setAnalysisStep('');
    setExecutionError(null);
  }, []);

  // Cleanup effect
  const cleanup = useCallback(() => {
    setIsAnalyzing(false);
    setIsCancelled(false);
  }, []);

  // Auto-cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    // State
    isAnalyzing,
    analysisProgress,
    analysisStep,
    executionError,
    lastExecutionTime,

    // Actions
    runAnalysis,
    cancelAnalysis,
    clearExecutionError,
    resetExecutionState
  };
};